/**
 * ProgressBar Component
 *
 * Displays utilization rate, performance score, or percentage metrics
 */

interface ProgressBarProps {
  /** Label text */
  label?: string
  /** Progress value (0-100) */
  value: number | string
  /** Color variant */
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info'
  /** Show percentage text */
  showValue?: boolean
  /** Custom className */
  className?: string
}

export function ProgressBar({
  label,
  value,
  variant = 'default',
  showValue = true,
  className = '',
}: ProgressBarProps) {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value
  const clampedValue = Math.min(Math.max(numericValue, 0), 100)

  const variantColors = {
    default: 'bg-blue-600',
    success: 'bg-green-600',
    warning: 'bg-yellow-500',
    danger: 'bg-red-600',
    info: 'bg-cyan-600',
  }

  const bgColor = variantColors[variant] || variantColors.default

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          {showValue && (
            <span className="text-sm font-semibold text-gray-900">
              {clampedValue.toFixed(1)}%
            </span>
          )}
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div
          className={`${bgColor} h-2.5 rounded-full transition-all duration-300`}
          style={{ width: `${clampedValue}%` }}
        />
      </div>
    </div>
  )
}
